import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import countries from 'i18n-iso-countries';
import LanguageDetector from 'i18next-browser-languagedetector';
import intervalPlural from 'i18next-intervalplural-postprocessor';

// Use eager loading to bundle country locale files at build time
// This avoids cross-origin dynamic import issues when widget is loaded from another domain
const countryLanguages = import.meta.glob(
    '../../node_modules/i18n-iso-countries/langs/*.json',
    { eager: true }
);

// Eagerly load all locale files to avoid cross-origin dynamic import issues
const locales = import.meta.glob(
    '../locales/**/*.json',
    { eager: true }
);

const loadLanguageFile = (lang) => {
    // Find the matching language file from the eagerly loaded modules
    const langKey = Object.keys(countryLanguages).find(key => key.endsWith(`/${lang}.json`));
    
    if (langKey) {
        return countryLanguages[langKey].default || countryLanguages[langKey];
    }

    if (lang !== 'en') {
        console.warn(`Falling back to 'en' for countries locale (missing ${lang})`);
        return loadLanguageFile('en');
    }

    throw new Error(`Language file for '${lang}' not found`);
}

const loadLocale = (language, namespace) => {
    // Try exact match first
    let localeKey = Object.keys(locales).find(
        key => key.includes(`/${language}/`) && key.endsWith(`/${namespace}.json`)
    );
    
    // Fallback to en-GB
    if (!localeKey) {
        localeKey = Object.keys(locales).find(
            key => key.includes('/en-GB/') && key.endsWith(`/${namespace}.json`)
        );
    }
    
    if (localeKey) {
        return locales[localeKey].default || locales[localeKey];
    }
    
    return null;
}

i18n.use(intervalPlural)
    .use(LanguageDetector)
    .use({
        type: 'backend',
        read(language, namespace, callback) {
            let lang = language.split('-')[0];

            try {
                const langFile = loadLanguageFile(lang);
                countries.registerLocale(langFile);
            } catch (e) {
                console.error(e);
            }

            const localeData = loadLocale(language, namespace);
            if (localeData) {
                callback(null, localeData);
            } else {
                callback(new Error(`Locale ${language}/${namespace} not found`), null);
            }
        }
    })
    .use(initReactI18next)
    .init({
        load: 'currentOnly',
        fallbackLng: {
            'en-CA': ['en-US', 'en-GB'],
            'gb-GB': ['en-GB'],
            'en-US': ['en-GB'],
            'es-ES': ['en-GB'],
            'fr-CA': ['fr-FR', 'en-GB'],
            'fr-CH': ['fr-FR', 'en-GB'],
            'fr-FR': ['en-GB'],
            'it-IT': ['en-GB'],
            'de-CH': ['de-DE', 'en-GB'],
            'de-DE': ['de-CH', 'en-GB'],
            'nl-BE': ['nl-NL', 'en-GB'],
            'nl-NL': ['en-GB'],
            'ca-ES': ['es-ES', 'en-GB'],
            'br': ['fr-FR', 'en-GB'],
            'eu': ['fr-FR', 'en-GB'],
            'ru_RU': ['en-GB'],
            'pt_PT': ['en-GB'],
            'el_GR': ['en-GB'],
            'tr_TR': ['en-GB'],
            default: ['en-GB']
        },
        ns: 'index',
        defaultNS: 'index',
        interpolation: {
            escapeValue: false,
            prefix: '%',
            suffix: '%'
        },
        react: {
            useSuspense: true,
            transSupportBasicHtmlNodes: true
        },
        debug: import.meta.env.MODE !== 'production'
    });

window.i18n = i18n;
export default i18n;
